import {useLightMode} from "../context/ThemeContext";

// Login screen backgrounds: every image dropped into a genre's subfolder of
// src/assets/login-backgrounds (fantasy/, scifi/, horror/, postapoc/, pirate/, urban/, japan/, cyberpunk/, primal/) is bundled
// automatically (webpack's require.context), and one is picked at random per page load. No
// list to keep in sync - add or remove files and rebuild (the dev server picks them up live).
// Record each image's artist, source and license in that folder's SOURCES.md.
const loadAll = (context) => context.keys().map(key => context(key));
const BACKGROUNDS = {
    fantasy: loadAll(require.context('../assets/login-backgrounds/fantasy', false, /\.(jpe?g|png|webp|gif|avif)$/i)),
    scifi: loadAll(require.context('../assets/login-backgrounds/scifi', false, /\.(jpe?g|png|webp|gif|avif)$/i)),
    horror: loadAll(require.context('../assets/login-backgrounds/horror', false, /\.(jpe?g|png|webp|gif|avif)$/i)),
    postapoc: loadAll(require.context('../assets/login-backgrounds/postapoc', false, /\.(jpe?g|png|webp|gif|avif)$/i)),
    pirate: loadAll(require.context('../assets/login-backgrounds/pirate', false, /\.(jpe?g|png|webp|gif|avif)$/i)),
    urban: loadAll(require.context('../assets/login-backgrounds/urban', false, /\.(jpe?g|png|webp|gif|avif)$/i)),
    japan: loadAll(require.context('../assets/login-backgrounds/japan', false, /\.(jpe?g|png|webp|gif|avif)$/i)),
    cyberpunk: loadAll(require.context('../assets/login-backgrounds/cyberpunk', false, /\.(jpe?g|png|webp|gif|avif)$/i)),
    primal: loadAll(require.context('../assets/login-backgrounds/primal', false, /\.(jpe?g|png|webp|gif|avif)$/i)),
};

// Chosen once at module load, so the image holds steady while the user moves between the
// login screen and back within the same page load, and changes on the next refresh.
const pick = (list) => (list.length ? list[Math.floor(Math.random() * list.length)] : null);
const PICKED = Object.fromEntries(Object.entries(BACKGROUNDS).map(([genre, list]) => [genre, pick(list)]));

export const loginBackgroundFor = (genre) => PICKED[genre] ?? null;

/** Inline style for a `.login` page's background in the current genre. Undefined when the genre
 *  has no images, so LoginForm.css's own background shows (a starfield for Sci-Fi, a blood moon for Horror, a
 *  dust storm for Post-Apocalyptic, a sunset at sea for Pirate, a neon-lit night city for Urban Fantasy,
 *  a neon megacity for Cyberpunk, a firelit cave for Primal).
 *  Photos get a light blur (LoginForm.css's .login::before), lighter than the Tavern's 3px so
 *  the art stays crisp; the painted fallbacks get none. Setting the variable here, not just in
 *  .login, is what overrides a genre fallback rule's 0px when that genre does have a photo. */
const LOGIN_BG_BLUR = '1px';
export function useLoginBackgroundStyle() {
    const {genre} = useLightMode();
    const image = loginBackgroundFor(genre);
    return image ? {background: `url(${image}) no-repeat center / cover`, '--login-bg-blur': LOGIN_BG_BLUR} : undefined;
}
